import React, { useMemo, useState } from "react";
import Form from "react-bootstrap/Form";
import SwLoader from "../SwLoader/SwLoader";
import SwNoData from "../NoData/NoData";


const SwTable = ({
  columns = [],
  data = [],
  loading = false,

  /* Pagination */
  pagination = true,
  defaultEntriesPerPage = 10,

  /* Selection */
  selectable = false,
  onSelectionChange,

  /* Server side */
  serverSide = false,
  totalRecords,

  className = "",
}) => {
  /* ---------------- State ---------------- */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(defaultEntriesPerPage);
  const [sortConfig, setSortConfig] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  /* ---------------- Sorting ---------------- */
  const sortedData = useMemo(() => {
    if (serverSide || !sortConfig) return data;

    const { key, direction } = sortConfig;

    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, serverSide]);

  const handleSort = (col) => {
    if (!col.sortable) return;

    setSortConfig((prev) => {
      if (!prev || prev.key !== col.key) {
        return { key: col.key, direction: "asc" };
      }
      return {
        key: col.key,
        direction: prev.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  /* ---------------- Pagination ---------------- */
  const total = serverSide ? totalRecords : sortedData.length;
  const totalPages = Math.ceil(total / perPage);

  const paginatedData = useMemo(() => {
    if (!pagination || serverSide) return sortedData;
    const start = (page - 1) * perPage;
    return sortedData.slice(start, start + perPage);
  }, [sortedData, page, perPage, pagination, serverSide]);

  /* ---------------- Selection ---------------- */
  const toggleRow = (row) => {
    let updated;
    if (selectedRows.includes(row)) {
      updated = selectedRows.filter((r) => r !== row);
    } else {
      updated = [...selectedRows, row];
    }
    setSelectedRows(updated);
    onSelectionChange?.(updated);
  };

  const allVisibleRowsSelected =
    paginatedData.length > 0 &&
    paginatedData.every((row) => selectedRows.includes(row));

  const someVisibleRowsSelected =
    paginatedData.some((row) => selectedRows.includes(row)) &&
    !allVisibleRowsSelected;

  const toggleSelectAll = () => {
    let updated;

    if (allVisibleRowsSelected) {
      updated = selectedRows.filter(
        (row) => !paginatedData.includes(row)
      );
    } else {
      updated = Array.from(
        new Set([...selectedRows, ...paginatedData])
      );
    }

    setSelectedRows(updated);
    onSelectionChange?.(updated);
  };

  /* ---------------- Render ---------------- */
  return (
    <div className={`SwTable ${className}`}>
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              {selectable && (
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={allVisibleRowsSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleRowsSelected;
                    }}
                    onChange={toggleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}

              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col)}
                  className={col.sortable ? "cursor-pointer" : ""}
                >
                  <span className="d-inline-flex align-items-center">
                    {col.label}

                    {col.sortable && (
                      <span className="ms-1">
                        {sortConfig?.key === col.key ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-chevron-up"></i>
                          ) : (
                            <i className="bi bi-chevron-down"></i>
                          )
                        ) : (
                          <i className="bi bi-chevron-expand opacity-50"></i>
                        )}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
          {/* Loading State */}
          {loading ? (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="text-center py-5"
              >
                <SwLoader />
              </td>
            </tr>
          ) : paginatedData.length ? (
            paginatedData.map((row, idx) => (
              <tr key={idx}>
                {selectable && (
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedRows.includes(row)}
                      onChange={() => toggleRow(row)}
                    />
                  </td>
                )}

                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="py-5"
              >
                <SwNoData message="No Data available" />
              </td>
            </tr>
          )}
        </tbody>

        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="SwPagination">
          <div className="PerPageEntry">
            <span>Entries per page</span>
            <Form.Select
              className="SwFormSelect"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="Pagination_ctrl ms-auto">
            <div>
              Showing{" "}
              <strong>{(page - 1) * perPage + 1}</strong> –{" "}
              <strong>{Math.min(page * perPage, total)}</strong> of{" "}
              <strong>{total}</strong> entries
            </div>

            <div className="Pagination_nav">
              <button
                className="btn PrevPagination"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <i className="bi bi-chevron-left"></i>
              </button>

              <Form.Control
                className="SwFormText"
                type="text"
                value={page}
                readOnly
              />

              <button
                className="btn NextPagination"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwTable;
